import { Loader, useMantineTheme, Text, Button, Flex } from "@mantine/core";
import { memo, useContext, useEffect, useState } from "react";
import { AppContext } from "src/api/AppContext";
import { CatalogContext, UnsavedMethod } from "src/api/context/CatalogContext";
import { ISelectedPayment } from "src/components/PaymentSelection";
import {
  TransactionType,
  useCreateTransactionMutation,
  GetRevenueByLocationQuery,
  useGetRevenueByLocationQuery,
  useGetLocationRevenueQuery,
} from "src/generated/graphql";
import { guid_to_base64 } from "src/utils/utils";
import { Cart, ProductSummary } from "./Checkout";
import { v4 as uuidv4 } from "uuid";
import { useModals } from "@mantine/modals";
import { currencyFormatter } from "src/utils/formatting";
import React from "react";
import NumberInput from "src/components/NumberInput/NumberInput";
import { ShoppingCartMethods } from "src/utils/constants";

type Props = {
  cart: Cart;
  shoppingCartId: string;
  serviceLocId?: string | null;
  onComplete?: () => void;
};

const sha512 = async (str: string) => {
  const buf = await crypto.subtle.digest(
    "SHA-512",
    new TextEncoder().encode(str)
  );
  return Array.prototype.map
    .call(new Uint8Array(buf), (x) => ("00" + x.toString(16)).slice(-2))
    .join("");
};

const getDate = () => {
  let date = new Date(new Date().toUTCString());
  let year = new Intl.DateTimeFormat("en", { year: "numeric" }).format(date);
  let month = new Intl.DateTimeFormat("en", { month: "2-digit" }).format(date);
  let day = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(date);
  let hour = new Intl.DateTimeFormat("en", {
    hour: "2-digit",
    hour12: false,
  }).format(date);
  let min = new Intl.DateTimeFormat("en", { minute: "2-digit" }).format(date);
  let sec = new Intl.DateTimeFormat("en", { second: "2-digit" }).format(date);
  return `${day}-${month}-${year}:${hour}:${min}:${sec}:000`;
};

const PayrocHostedTransaction = ({
  cart,
  shoppingCartId,
  serviceLocId,
  onComplete,
}: Props) => {
  const theme = useMantineTheme();
  const { currentEmployee, currentLocation } = useContext(AppContext);
  const {
    selectedCustomer,
    selectedPayment,
    selectedUnsavedMethod,
    resetState,
  } = useContext(CatalogContext);
  const modals = useModals();
  const [date, setDate] = useState<string | undefined>(undefined);
  const [orderId, setOrderId] = useState<string | undefined>(undefined);
  const [hash, setHash] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isInitiated, setIsInitiated] = useState<boolean>(false);

  const [cashPaid, setCashPaid] = useState<number>(0);
  const [changeDue, setChangeDue] = useState<number | undefined>(undefined);
  const [cashLoading, setCashLoading] = useState<boolean>(false);

  const [createTransaction] = useCreateTransactionMutation();
  const { data, loading } = useGetRevenueByLocationQuery({
    variables: { locationId: currentLocation?.id || "" },
    skip: !currentLocation,
  });

  const receiptPageUrl = (
    process.env.REACT_APP_BASE_URL ||
    "https://sindro-qa.azurewebsites.net/graphql"
  ).split("/graphql")[0];

  const getHash = async (
    date: string,
    orderId: string,
    amount: number,
    terminalId: string
  ) => {
    let toHash = `${terminalId}:${orderId}:${amount}:${date}:${
      receiptPageUrl + "/webhooks/payroc/receipt-page-url"
    }:${data?.locationRevenueByLocation.payrocAccount?.payrocEmbeddedSecret}`;
    return await sha512(toHash);
  };

  const setup = async (
    cart: Cart,
    clientId: string,
    selectedPayment: ISelectedPayment,
    selectedUnsavedMethod?: UnsavedMethod,
    terminalId?: string
  ) => {
    if (!shoppingCartId) return;
    let transaction = await createTransaction({
      variables: {
        data: {
          performedBy: currentEmployee ? { id: currentEmployee.id } : undefined,
          location: { id: serviceLocId || currentLocation?.id || "" },
          isRecurring: false,
          targetClient: { id: clientId },
          //paymentTerminal: terminalId ? { id: terminalId } : undefined,
          paymentMethod: selectedPayment
            ? { id: selectedPayment.id }
            : undefined,
          shoppingCart: { id: shoppingCartId },
        },
      },
    });

    if (transaction.data?.createTransaction && selectedPayment) {
      modals.closeModal("secureTransactionModal");
      resetState();
      TransactionSuccess();
      ShoppingCartMethods.remove();
    } else {
      if (transaction.data?.createTransaction) {
        let _date = getDate();
        setDate(_date);
        let uuid = transaction.data.createTransaction.id;
        let _orderId = guid_to_base64(uuid);
        setOrderId(_orderId);
        let _hash = await getHash(
          _date,
          _orderId,
          cart.total || 0,
          data?.locationRevenueByLocation.payrocAccount
            ?.payrocVirtualTerminalId || ""
        );
        setHash(_hash);
      }
    }
  };

  const TransactionSuccess = () => {
    modals.openModal({
      onClose: () => onComplete && onComplete(),
      closeOnClickOutside: false,
      title: "Secure Transaction",
      children: (
        <div>
          <Text fw={500} align="center">
            {"Transaction Successful!"}
          </Text>
        </div>
      ),
      styles: {
        modal: {
          backgroundColor: "rgba(0,0,0,0) !important",
        },
        header: {
          backgroundColor: theme.colors.primary[6],
          color: theme.colors.primaryTextColor[6],
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        },
        body: {
          color: theme.colors.secondaryTextColor[6],
          backgroundColor: theme.colors.secondary[6],
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
        },
      },
    });
  };

  const processCashPayment = async (clientId: string, paid: number) => {
    setCashLoading(true);
    if (!shoppingCartId) return;

    let transaction = await createTransaction({
      variables: {
        data: {
          performedBy: currentEmployee ? { id: currentEmployee.id } : undefined,
          location: { id: serviceLocId || currentLocation?.id || "" },
          isRecurring: false,
          targetClient: { id: clientId },
          paymentType: TransactionType.Cash,
          ...(shoppingCartId && { shoppingCart: { id: shoppingCartId } }),
        },
      },
    });
    if (transaction.data?.createTransaction) {
      setChangeDue(Math.round((paid - (cart.total || 0)) * 100) / 100);
      setCashLoading(false);
      resetState();
      // ChangeDue 0 means completed
      if (!changeDue) {
        modals.closeModal("secureTransactionModal");
        ShoppingCartMethods.remove();
        TransactionSuccess();
      }
    }
  };

  useEffect(() => {
    if (
      cart &&
      selectedCustomer?.client &&
      (selectedPayment || selectedUnsavedMethod) &&
      !loading
    )
      if (selectedUnsavedMethod !== UnsavedMethod.Cash) {
        setup(
          cart,
          selectedCustomer.client.id,
          selectedPayment,
          selectedUnsavedMethod,
          process.env.env === "prod"
            ? data?.locationRevenueByLocation.payrocAccount
                ?.payrocVirtualTerminalId || undefined
            : undefined
        );
      }
  }, [cart, selectedCustomer, selectedPayment, selectedUnsavedMethod, loading]);

  useEffect(() => {
    if (isLoading && hash && orderId && date) {
      setIsLoading(false);
    }
  }, [hash, orderId, date, isLoading]);

  if (selectedUnsavedMethod === UnsavedMethod.Cash) {
    return (
      <div
        style={{
          color: theme.colors.secondaryTextColor[6],
        }}
      >
        {cashLoading ? (
          <Loader />
        ) : changeDue ? (
          <React.Fragment>
            <Text>{`The transaction was processed successfully!`}</Text>
            <br />
            <Text>{`The total change due is: ${currencyFormatter.format(
              changeDue
            )}`}</Text>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Text>{`The total is: ${currencyFormatter.format(
              cart.total || 0
            )}`}</Text>
            <br />
            <Text>{`Cash payment has been selected. Please enter the total cash value paid to complete transaction and calculate change.`}</Text>
            <br />
            <NumberInput
              mt={0}
              size="sm"
              value={cashPaid}
              formatter={(amount) => {
                if (!amount) {
                  return "$";
                }
                let out = amount;
                if (out.includes(".")) {
                  let split = out.split(".");
                  if (split[1].length > 2) {
                    out = split[0] + "." + split[1].substring(0, 2);
                  }
                }
                return (
                  "$" + out.replace(/(\d)(?=(\d{3})+(\.(\d){0,2})*$)/g, "$1,")
                );
              }}
              parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
              precision={2}
              onChange={(e) => setCashPaid(e || 0)}
            />
            <br />
            <Flex justify={"center"}>
              <Button
                size="sm"
                sx={{
                  height: 34,
                  backgroundColor:
                    cashPaid < (cart.total || 0)
                      ? `${theme.colors.secondary[6]} !important`
                      : undefined,
                  color:
                    cashPaid < (cart.total || 0)
                      ? `${theme.colors.secondary[1]} !important`
                      : undefined,
                }}
                onClick={() =>
                  processCashPayment(
                    selectedCustomer?.client?.id || "",
                    cashPaid
                  )
                }
                disabled={cashPaid < (cart.total || 0)}
                loading={loading}
              >
                Submit Payment
              </Button>
            </Flex>
          </React.Fragment>
        )}
      </div>
    );
  }

  return (
    <div>
      {!isInitiated && (
        <Text>{`Press "Continue" below to continue processing the secure transaction.`}</Text>
      )}
      <iframe
        title="transactionSubIframe"
        name="securePayment"
        style={{
          width: "100%",
          height: isInitiated ? 660 : 20,
          opacity: isInitiated ? 1 : 0,
          border: "none",
          borderRadius: 4,
          overflow: "hidden",
        }}
      />
      {isLoading ? (
        <Loader />
      ) : selectedPayment ? (
        <div></div>
      ) : (
        <form
          name="securePaymentSetup"
          action="https://testpayments.worldnettps.com/merchant/paymentpage"
          method="post"
          target="securePayment"
          style={{
            height: isInitiated ? 0 : undefined,
            opacity: isInitiated ? 0 : 1,
          }}
        >
          <input
            type="hidden"
            name="TERMINALID"
            value={
              data?.locationRevenueByLocation.payrocAccount
                ?.payrocVirtualTerminalId || ""
            }
          />
          <input type="hidden" name="ORDERID" value={orderId} />
          <input type="hidden" name="CURRENCY" value="USD" />
          <input type="hidden" name="AMOUNT" value={cart?.total || 0} />
          <input type="hidden" name="DATETIME" value={date} />
          <input
            type="hidden"
            name="RECEIPTPAGEURL"
            value={receiptPageUrl + "/webhooks/payroc/receipt-page-url"}
          />
          <input type="hidden" name="HASH" value={hash} />
          <input type="hidden" name="INIFRAME" value={"Y"} />
          <input type="hidden" name="DESCRIPTION" value={"Desc..."} />
          {selectedCustomer?.client && (
            <input
              type="hidden"
              name="OTHERFIELD"
              value={selectedCustomer?.client?.id}
            />
          )}
          {/* {selectedPayment?.externalId && (
            <input
              type="hidden"
              name="CARDREFERENCE"
              value={selectedPayment.externalId}
            />
          )} */}
          <input type="hidden" name="SECURECARDMERCHANTREF" value={uuidv4()} />
          {selectedUnsavedMethod === UnsavedMethod.Card && (
            <input type="hidden" name="PAYMENTOPTIONS" value={"CARD"} />
          )}
          {selectedUnsavedMethod === UnsavedMethod.ACH && (
            <input
              type="hidden"
              name="PAYMENTOPTIONS"
              value={"BANK_TRANSFER"}
            />
          )}
          {/* {selectedUnsavedMethod && <input type="hidden" name="SECURECARDMERCHANTREF" value={merchantRef} />} */}
          {/* {cart?.products.map((product, index) => {
            let totalTaxRate = product.taxRates ? product.taxRates.map(taxRate => taxRate.taxRate).reduce((a,b) => a+b, 0) : 0;
            let base = product.isPostTax ? product.cost/(1+totalTaxRate) : product.cost;
            let subTotal = base * product.count;
            let tax = subTotal * totalTaxRate;
            let total = subTotal + tax;
            return (
              <div key={`line-item-${index + "-" + product.id}`}>
                <input
                  type="hidden"
                  name={`LINE_ITEM_${index}_DESCRIPTION`}
                  value={product.name}
                />
                <input
                  type="hidden"
                  name={`LINE_ITEM_${index}_QUANTITY`}
                  value={product.count}
                />
                <input
                  type="hidden"
                  name={`LINE_ITEM_${index}_UNIT_PRICE`}
                  value={base}
                />
                <input
                  type="hidden"
                  name={`LINE_ITEM_${index}_TAX_RATE`}
                  value={totalTaxRate}
                />
                <input
                  type="hidden"
                  name={`LINE_ITEM_${index}_TOTAL_AMOUNT`}
                  value={total}
                />
                <input type="hidden" name="DATETIME" value={date} />
                <input type="hidden" name="HASH" value={hash} />
              </div>
            );
          })} */}
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <input
              type="submit"
              value="Continue"
              style={{
                border: "none",
                borderRadius: 4,
                padding: "8px 20px",
                backgroundColor: theme.colors.primary[6],
                color: theme.colors.primaryTextColor[6],
              }}
              onClick={() => {
                setIsInitiated(true);
              }}
            />
          </div>
        </form>
      )}
    </div>
  );
};

export default memo(PayrocHostedTransaction);
